<script setup lang="ts">
import { default as PDatatable } from 'primevue/datatable'
import { default as PColumn } from 'primevue/column'
import { inject, onMounted, type Ref, ref } from 'vue'
import { default as PButton } from 'primevue/button'
import type { SchoolPeriodUser } from '@/core/domain/school/user/SchoolPeriodUser.ts'
import { useConfirm } from 'primevue/useconfirm'
import { useUsersStore } from '@/presentation/stores/users/users.ts'

// Type
interface RowUserData {
  identifier: string
  firstname: string
  lastname: string
  username: string
  password: string | null
}

// Composables
const confirm = useConfirm()
const usersStore = useUsersStore()

// Store Refs
const { showSuccessToast, showInfoToast } = usersStore

// Refs
const dialogRef: any = inject('dialogRef')
const title: Ref<string> = ref('')
const isStudentList: Ref<boolean> = ref(false)
const users: Ref<RowUserData[]> = ref([])

const confirmPopup = (event: MouseEvent, row: RowUserData) => {
  const target = event.currentTarget as HTMLElement | undefined
  confirm.require({
    target,
    message: 'Êtes-vous sûr de vouloir réinitialiser le mot de passe cet utilisateur',
    icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Annuler',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Réinitialiser'
    },
    accept: async () => {
      row.password = await usersStore.resetPassword(row.identifier)
      showSuccessToast('Réinitialisation du mot de passe', 'Le mot de passe à été réinitialisé.')
    },
    reject: () => {
      showInfoToast(
        'Réinitialisation du mot de passe',
        'Abandon de la réinitialisation du mot de passe.'
      )
    }
  })
}

onMounted(() => {
  title.value = dialogRef.value?.data?.title
  users.value = dialogRef.value?.data?.users ?? []
  isStudentList.value = dialogRef.value?.data?.isStudentList ?? false

  users.value = dialogRef.value?.data?.users.map((u: SchoolPeriodUser) => ({
    identifier: u.userIdentifier,
    firstname: u.firstname,
    lastname: u.lastname,
    username: u.username,
    password: null
  }))
})
</script>

<template>
  <div class="grid-cols-12= grid gap-4">
    <p-datatable :value="users" class="col-span-12" resizable-columns column-resize-mode="fit" data-maskable>
      <p-column header="Prénom" field="firstname" />
      <p-column header="Nom" field="lastname" />
      <p-column header="Username" field="username" />
      <p-column header="Mot de passe" v-if="isStudentList" :exportable="false">
        <template #body="slotProps: { data: RowUserData }">
          <div class="flex flex-row items-center gap-2">
            <p class="w-80 min-w-0 font-mono tracking-normal slashed-zero">
              {{ slotProps.data.password }}
            </p>
            <p-button
              outlined
              size="small"
              icon="pi pi-sync"
              label="Changer le mot de passe"
              class="ml-auto w-fit shrink-0"
              aria-label="Changer de mot de passe"
              @click.stop.prevent="(e) => confirmPopup(e, slotProps.data)"
              v-tooltip="{ value: 'Changer le mot de passe', pt: { text: 'text-sm' } }"
            />
          </div>
        </template>
      </p-column>
    </p-datatable>
  </div>
</template>
